import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { canOpenLessonRequests } from "@wmf/shared/capabilities";
import { get, getStudentClashes, send, type Me, type Queue as QueueData } from "./api.js";
import { sortExceptions } from "./booking/exceptions.js";
import { scopeChoice } from "./booking/scope.js";
import { staffBooking } from "./booking/staff.js";
import { CreditReviewQueue } from "./CreditReviewQueue.js";
import { isoDay } from "./format.js";
import { useLiveShellRefresh } from "./liveRefresh.js";
import { useAsync } from "./useAsync.js";
import { waitlistApi } from "./waitlist/api.js";
import { CARD_RECORD_PAGE_SIZE } from "./paginationPageSizes.js";

export type ActionCentreTab =
  | "requests"
  | "clashes"
  | "exceptions"
  | "credits"
  | "waitlist"
  | "unmatched"
  | "student-reviews"
  | "bugs";
export const ACTION_CENTRE_PAGE_SIZE = CARD_RECORD_PAGE_SIZE;

function positivePage(value: string | null): number {
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : 1;
}

export interface ActionCentreAccess {
  requests: boolean;
  reports: boolean;
  credits: boolean;
  waitlist: boolean;
  unmatched: boolean;
  studentReviews?: boolean;
  bugs?: boolean;
}

export const ACTION_CENTRE_TABS: Array<{
  id: ActionCentreTab;
  label: string;
  access: keyof ActionCentreAccess;
}> = [
  { id: "requests", label: "Requests", access: "requests" },
  { id: "clashes", label: "Clashes", access: "reports" },
  { id: "exceptions", label: "Exceptions", access: "reports" },
  { id: "credits", label: "Credits", access: "credits" },
  { id: "waitlist", label: "Waiting list", access: "waitlist" },
  { id: "unmatched", label: "Unmatched", access: "unmatched" },
  { id: "student-reviews", label: "Students & family links", access: "studentReviews" },
  { id: "bugs", label: "Bugs", access: "bugs" },
];

export function actionCentreAccess(me: Me): ActionCentreAccess {
  const held = new Set(me.capabilities);
  const bookingStaff = me.booking.staff;
  return {
    requests: bookingStaff && canOpenLessonRequests(held),
    reports: bookingStaff && held.has("reports.operations.read"),
    credits:
      bookingStaff &&
      (held.has("lesson_blocks.manage.all") || Boolean(me.linkedToProvider)),
    waitlist:
      bookingStaff && (me.roles.includes("owner") || me.roles.includes("tutor")),
    // Attribution is an /admin read and only has a useful contract when the
    // staff booking integration is mounted. Do not expose the consolidated
    // workspace (or issue this read globally) to disconnected staff accounts.
    unmatched: bookingStaff,
    studentReviews: me.roles.includes("owner"),
    bugs: me.roles.includes("owner"),
  };
}

export function availableActionCentreTabs(access: ActionCentreAccess) {
  return ACTION_CENTRE_TABS.filter((tab) => access[tab.access]);
}

export function hasActionCentreAccess(access: ActionCentreAccess): boolean {
  return availableActionCentreTabs(access).length > 0;
}

export function resolveActionCentreTab(
  requested: string | null,
  access: ActionCentreAccess,
): ActionCentreTab | null {
  const tabs = availableActionCentreTabs(access);
  if (tabs.some((tab) => tab.id === requested)) return requested as ActionCentreTab;
  return tabs[0]?.id ?? null;
}

export function unmatchedAttentionCount(data: QueueData | null | undefined): number {
  if (!data) return 0;
  return data.summary ? data.summary.readyGroups + data.summary.unidentifiedGroups
    : data.needsDecision.length + data.unidentified.groups;
}

export function aggregateActionCentreCounts(
  counts: Partial<Record<ActionCentreTab, number | null>>,
  tabs: readonly ActionCentreTab[],
): number | null {
  const visible = tabs.map((tab) => counts[tab] ?? null);
  if (visible.some((count) => count === null)) return null;
  return visible.reduce<number>((total, count) => total + (count ?? 0), 0);
}

type AsyncState<T> = {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
};

type RequestsReport = Awaited<ReturnType<typeof staffBooking.lessonRequests>>;
type ClashReport = Awaited<ReturnType<typeof getStudentClashes>>;
type ExceptionReport = Awaited<ReturnType<typeof staffBooking.exceptions>>;
type WaitlistReport = Awaited<ReturnType<typeof waitlistApi.requests>>;

type CountResponse = {
  // Optional only for backwards-compatible test/mocked responses. The real
  // endpoint always returns both objects, but an absent value is safely an
  // unknown count rather than a reason to crash the whole staff shell.
  counts?: Partial<Record<ActionCentreTab, number | null>>;
  totals?: Partial<Record<ActionCentreTab, number | null>>;
};

export interface ActionCentreState {
  access: ActionCentreAccess;
  /** The authenticated provider identity; never guess ownership from student identity. */
  requestViewer: {
    providerId: number | null;
    displayName: string | null;
    canSeeTutorDirectory: boolean;
  };
  requestScope: "mine" | "organisation";
  waitlistScope: "mine" | "organisation";
  canChooseRequestScope: boolean;
  setRequestScope: (scope: "mine" | "organisation") => void;
  setWaitlistScope: (scope: "mine" | "organisation") => void;
  requests: AsyncState<RequestsReport>;
  clashes: AsyncState<ClashReport>;
  exceptions: AsyncState<ExceptionReport | null>;
  waitlist: AsyncState<WaitlistReport>;
  unmatched: AsyncState<QueueData>;
  actionableRequestIds: ReadonlySet<string>;
  exceptionGroups: ReturnType<typeof sortExceptions>;
  exceptionScope: "mine" | "organisation";
  exceptionScopeChoice: ReturnType<typeof scopeChoice>;
  setExceptionScope: (scope: "mine" | "organisation") => void;
  countFor: (tab: ActionCentreTab) => number | null;
  total: number | null;
  loading: boolean;
  failed: boolean;
  reviewing: boolean;
  reviewAll: () => Promise<void>;
  reloadAll: () => void;
}

const ActionCentreContext = createContext<ActionCentreState | null>(null);

const EMPTY_UNMATCHED = {
  needsDecision: [],
  addedByTutors: [],
  unidentified: { bookings: 0, groups: 0, sample: [] },
  truncated: false,
} as unknown as QueueData;

function withSummaryReload<T>(state: AsyncState<T>, reloadSummary: () => void): AsyncState<T> {
  return {
    ...state,
    reload: () => {
      state.reload();
      reloadSummary();
    },
  };
}

function ActionCentreReviewControl({
  visible,
  total,
  reviewing,
  onReview,
}: {
  visible: boolean;
  total: number | null;
  reviewing: boolean;
  onReview: () => Promise<void>;
}) {
  const [host, setHost] = useState<Element | null>(null);

  useEffect(() => {
    if (!visible || total === null || total === 0) {
      setHost(null);
      return;
    }

    const findHost = () => {
      const next = document.querySelector(".action-centre-page .operations-head");
      setHost((current) => (current === next ? current : next));
      return next !== null;
    };

    if (findHost()) return;
    const observer = new MutationObserver(() => {
      if (findHost()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [visible, total]);

  if (!visible || total === null || total === 0 || host === null) return null;

  return createPortal(
    <button
      type="button"
      className="quiet"
      disabled={reviewing}
      aria-label={`Mark ${total} current Action Centre item${total === 1 ? "" : "s"} reviewed`}
      onClick={() => void onReview()}
    >
      {reviewing ? "Marking reviewed…" : "Mark all reviewed"}
    </button>,
    host,
  );
}

function CreditReviewDetailPortal({
  visible,
  onChanged,
}: {
  visible: boolean;
  onChanged: () => void;
}) {
  const [host, setHost] = useState<Element | null>(null);

  useEffect(() => {
    if (!visible) {
      setHost(null);
      return;
    }

    const findHost = () => {
      const next = document.querySelector(".action-centre-page .operations-content");
      setHost((current) => (current === next ? current : next));
      return next !== null;
    };

    if (findHost()) return;
    const observer = new MutationObserver(() => {
      if (findHost()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [visible]);

  if (!visible || host === null) return null;
  return createPortal(<CreditReviewQueue onChanged={onChanged} />, host);
}

export function ActionCentreProvider({ me, children }: { me: Me; children: ReactNode }) {
  const access = actionCentreAccess(me);
  const location = useLocation();
  const exceptionScopeChoice = scopeChoice(me);
  const [exceptionScope, setExceptionScope] = useState<"mine" | "organisation">(
    exceptionScopeChoice.initial,
  );
  const canChooseRequestScope = me.roles.includes("owner") || me.capabilities.includes("lesson_requests.manage.all");
  const [requestScope, setRequestScope] = useState<"mine" | "organisation">(me.roles.includes("owner") ? "mine" : "organisation");
  const [waitlistScope, setWaitlistScope] = useState<"mine" | "organisation">(me.roles.includes("owner") ? "mine" : "organisation");
  const [reviewing, setReviewing] = useState(false);
  const today = isoDay(0);
  const visibleTabs = availableActionCentreTabs(access).map((tab) => tab.id);
  const requestedTab = new URLSearchParams(location.search).get("queue");
  const detailParams = new URLSearchParams(location.search);
  const detailPage = positivePage(detailParams.get("page"));
  const detailAddedPage = positivePage(detailParams.get("added_page"));
  const detailBookingId = detailParams.get("amelia_booking_id");
  const detailAppointmentId = detailParams.get("amelia_appointment_id");
  const unmatchedPeriod = detailParams.get("unmatched_period") === "all" ? "all" : "upcoming";
  const detailTab =
    location.pathname === "/waiting" ? resolveActionCentreTab(requestedTab, access) : null;

  // The shell needs compact counts, not every record collection. Counts are the
  // only Action Centre read that mounts globally; detail stays dormant until
  // the user actually opens a queue.
  const summary = useAsync<CountResponse>(
    () =>
      visibleTabs.length > 0
        ? get<CountResponse>("/admin/action-centre/counts")
        : Promise.resolve({ counts: {}, totals: {} }),
    [visibleTabs.join(",")],
    // Two count categories (clashes and unmatched mirror rows) can change after
    // a diary refresh, so this compact summary is one of the few intentional
    // mirror subscribers.
    { reloadOnMirrorRefresh: true },
  );
  useLiveShellRefresh(summary.reload);

  const requestsRaw = useAsync(
    () =>
      access.requests && (detailTab === "requests" || detailTab === "exceptions")
        ? staffBooking.lessonRequests(
            "pending_tutor_approval",
            detailPage,
            ACTION_CENTRE_PAGE_SIZE,
            canChooseRequestScope && detailTab === "requests" ? requestScope : undefined,
          )
        : Promise.resolve({ requests: [], total: 0 } as RequestsReport),
    [access.requests, detailTab, detailPage, requestScope, canChooseRequestScope],
  );
  const actionableRequestIds = new Set(
    (requestsRaw.data?.requests ?? []).map((request) => request.id),
  );

  const clashesRaw = useAsync(
    () =>
      access.reports && detailTab === "clashes"
        ? getStudentClashes(detailPage, ACTION_CENTRE_PAGE_SIZE)
        : Promise.resolve({ clashes: [] } as ClashReport),
    [access.reports, detailTab, detailPage],
    // A visible clash report is mirror-derived and should move with a manual
    // diary refresh; inactive tabs remain dormant.
    { reloadOnMirrorRefresh: detailTab === "clashes" },
  );

  const exceptionsRaw = useAsync<ExceptionReport | null>(
    () =>
      access.reports && detailTab === "exceptions" && exceptionScopeChoice.possible
        ? staffBooking.exceptions({
            start_date: isoDay(-30),
            end_date: today,
            scope: exceptionScope,
            page: detailPage,
            limit: ACTION_CENTRE_PAGE_SIZE,
            exclude_pending_requests: true,
          })
        : Promise.resolve(null),
    [
      access.reports,
      detailTab,
      detailPage,
      exceptionScopeChoice.possible,
      exceptionScope,
      today,
    ],
  );
  const exceptionGroups = sortExceptions(
    exceptionsRaw.data?.items ?? [],
    actionableRequestIds,
  );

  const waitlistRaw = useAsync(
    () =>
      access.waitlist && detailTab === "waitlist"
        ? waitlistApi.requests("pending", detailPage, ACTION_CENTRE_PAGE_SIZE, canChooseRequestScope ? waitlistScope : undefined)
        : Promise.resolve({ requests: [] } as WaitlistReport),
    [access.waitlist, detailTab, detailPage, waitlistScope, canChooseRequestScope],
  );

  const unmatchedRaw = useAsync(
    () =>
      access.unmatched && (detailTab === "unmatched" || detailTab === "student-reviews")
        ? get<QueueData>("/admin/attribution-queue", {
            period: unmatchedPeriod,
            page: String(detailPage),
            limit: String(ACTION_CENTRE_PAGE_SIZE),
            added_page: String(detailAddedPage),
            added_limit: "25",
            ...(detailBookingId !== null ? { amelia_booking_id: detailBookingId } : {}),
            ...(detailAppointmentId !== null ? { amelia_appointment_id: detailAppointmentId } : {}),
          })
        : Promise.resolve(EMPTY_UNMATCHED),
    [access.unmatched, detailTab, detailPage, detailAddedPage, detailBookingId, detailAppointmentId, unmatchedPeriod],
    // Attribution candidates are mirror rows too, but only the active queue
    // should refetch them after a diary refresh.
    { reloadOnMirrorRefresh: detailTab === "unmatched" || detailTab === "student-reviews" },
  );

  // A decision in the active queue changes both its detail and its badge.
  const requests = withSummaryReload(requestsRaw, summary.reload);
  const clashes = withSummaryReload(clashesRaw, summary.reload);
  const exceptions = withSummaryReload(exceptionsRaw, summary.reload);
  const waitlist = withSummaryReload(waitlistRaw, summary.reload);
  const unmatched = withSummaryReload(unmatchedRaw, summary.reload);

  function countFor(tab: ActionCentreTab): number | null {
    if (tab === "requests" && !access.requests) return null;
    if ((tab === "clashes" || tab === "exceptions") && !access.reports) return null;
    if (tab === "credits" && !access.credits) return null;
    if (tab === "waitlist" && !access.waitlist) return null;
    if (tab === "unmatched" && !access.unmatched) return null;

    // The global summary uses the account's default exception scope. Once the
    // user opens Exceptions and deliberately switches scope, the tab badge and
    // total should describe the list actually in front of them instead of the
    // still-useful shell summary for another scope.
    if (tab === "exceptions" && detailTab === "exceptions") {
      if (!exceptionScopeChoice.possible) return 0;
      if (exceptionsRaw.loading || exceptionsRaw.error) return null;
      return exceptionsRaw.data?.actionable_total ?? exceptionGroups.waiting.length;
    }

    // The all-lessons view includes historical work outside the upcoming
    // shell summary. Its tab total should describe the selected list.
    if (tab === "unmatched" && detailTab === "unmatched" && unmatchedPeriod === "all"
      && detailBookingId === null && detailAppointmentId === null) {
      if (unmatchedRaw.loading || unmatchedRaw.error) return null;
      return unmatchedAttentionCount(unmatchedRaw.data);
    }

    // Queue tabs continue to show the actual unresolved work. Acknowledgement
    // is deliberately only an attention/badge concern and must never make the
    // work itself appear to have gone away.
    return summary.data?.totals?.[tab] ?? summary.data?.counts?.[tab] ?? null;
  }

  const attentionCounts: Partial<Record<ActionCentreTab, number | null>> = {};
  for (const tab of visibleTabs) attentionCounts[tab] = summary.data?.counts?.[tab] ?? null;
  const total = aggregateActionCentreCounts(attentionCounts, visibleTabs);
  // Loading only until the first counts arrive. The summary refreshes every
  // 15 seconds and keeps its last answer while it does, so treating every
  // refresh as loading made the sidebar badge (and the Dashboard count) blink
  // out and back each time.
  const loading = visibleTabs.length > 0 && summary.loading && summary.data === null;
  const failed = Boolean(
    summary.error ||
      (!summary.loading && visibleTabs.some((tab) => attentionCounts[tab] === null)),
  );

  async function reviewAll() {
    if (reviewing || total === null || total === 0) return;
    setReviewing(true);
    try {
      await send("POST", "/admin/action-centre/acknowledge", {});
      summary.reload();
    } finally {
      setReviewing(false);
    }
  }

  function reloadAll() {
    summary.reload();
    if (detailTab === "requests") requestsRaw.reload();
    if (detailTab === "clashes") clashesRaw.reload();
    if (detailTab === "exceptions") {
      requestsRaw.reload();
      if (exceptionScopeChoice.possible) exceptionsRaw.reload();
    }
    if (detailTab === "waitlist") waitlistRaw.reload();
    if (detailTab === "unmatched") unmatchedRaw.reload();
  }

  return (
    <ActionCentreContext.Provider
      value={{
        access,
        requestViewer: {
          providerId: me.tutorIdentity?.providerId ?? null,
          displayName: me.tutorIdentity?.displayName ?? null,
          canSeeTutorDirectory:
            me.roles.includes("owner") ||
            (me.roles.includes("tutor") &&
              (me.capabilities.includes("appointments.manage.all") ||
                me.capabilities.includes("schedules.manage.all"))),
        },
        requests, requestScope, waitlistScope, canChooseRequestScope, setRequestScope, setWaitlistScope,
        clashes,
        exceptions,
        waitlist,
        unmatched,
        actionableRequestIds,
        exceptionGroups,
        exceptionScope,
        exceptionScopeChoice,
        setExceptionScope,
        countFor,
        total,
        loading,
        failed,
        reviewing,
        reviewAll,
        reloadAll,
      }}
    >
      {children}
      <ActionCentreReviewControl
        visible={location.pathname === "/waiting" && !loading && !failed}
        total={total}
        reviewing={reviewing}
        onReview={reviewAll}
      />
      <CreditReviewDetailPortal
        visible={detailTab === "credits"}
        onChanged={reloadAll}
      />
    </ActionCentreContext.Provider>
  );
}

export function useActionCentre(): ActionCentreState {
  const value = useContext(ActionCentreContext);
  if (!value) throw new Error("useActionCentre must be used inside ActionCentreProvider");
  return value;
}

export function ActionCentreSidebarBadge() {
  const { total, loading, failed } = useActionCentre();
  if (loading || failed || total === null || total === 0) return null;
  return (
    <span
      className="tag warn"
      title={`${total} unreviewed Action Centre item${total === 1 ? "" : "s"}`}
      aria-label={`${total} unreviewed Action Centre item${total === 1 ? "" : "s"}`}
    >
      {total}
    </span>
  );
}
